import React from 'react'
import { useSearchParams } from 'react-router-dom'

import { useAlerts } from '../context/alerts'
import { Alert } from '../types'

const ALERT_TYPES: Alert['type'][] = ['info', 'error', 'warning']

/** Dedupes alerts when React Strict Mode re-runs effects on the same URL. */
let lastAlertFromSearchParamsKey: string | null = null

export function appendAlertSearchParams(
  path: string,
  message: string,
  alertType: Alert['type'] = 'error'
): string {
  const [base, existingQuery] = path.split('?')
  const params = new URLSearchParams(existingQuery)
  params.set('message', message)
  params.set('alertType', alertType)
  return `${base}?${params.toString()}`
}

/** Show a toast from `?message=` and `?alertType=` then strip those params from the URL. */
export function useAlertFromSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { addAlert } = useAlerts()

  React.useEffect(() => {
    const message = searchParams.get('message')
    if (!message) {
      lastAlertFromSearchParamsKey = null
      return
    }

    const typeParam = searchParams.get('alertType')
    const type: Alert['type'] = ALERT_TYPES.includes(typeParam as Alert['type'])
      ? (typeParam as Alert['type'])
      : 'error'

    const dedupeKey = `${type}:${message}`
    if (lastAlertFromSearchParamsKey === dedupeKey) return
    lastAlertFromSearchParamsKey = dedupeKey

    addAlert({ type, message })

    const next = new URLSearchParams(searchParams)
    next.delete('message')
    next.delete('alertType')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, addAlert])
}
